"use client";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@components/ui/accordion";
import { Label } from "@components/ui/label";
import { RadioGroup, RadioGroupItem } from "@components/ui/radio-group";
import { baseURL } from "@repo/helpers";
import { Download } from "lucide-react";
import { useTranslations } from "next-intl";
import { Fragment, useState } from "react";

type DownloadType =
  | "walkingBase"
  | "walkingReverse"
  | "bikingBase"
  | "bikingReverse";

type Option = {
  label: "Hiking" | "Biking";
  values: {
    label: "northToSouth" | "southToNorth";
    id: DownloadType;
    value: DownloadType;
    fileName: string;
    url: string;
  }[];
};

const options: Option[] = [
  {
    label: "Hiking",
    values: [
      {
        label: "northToSouth",
        id: "walkingBase",
        value: "walkingBase",
        fileName: "Haervejen - Hiking - northToSouth",
        url: "base?type=hiking",
      },
      {
        label: "southToNorth",
        id: "walkingReverse",
        value: "walkingReverse",
        fileName: "Haervejen - Hiking - southToNorth",
        url: "reverse?type=hiking",
      },
    ],
  },
  {
    label: "Biking",
    values: [
      {
        label: "northToSouth",
        id: "bikingBase",
        value: "bikingBase",
        fileName: "Haervejen - Biking - northToSouth",
        url: "base?type=biking",
      },
      {
        label: "southToNorth",
        id: "bikingReverse",
        value: "bikingReverse",
        fileName: "Haervejen - Biking - southToNorth",
        url: "reverse?type=biking",
      },
    ],
  },
];

export function DownloadDialog() {
  const [val, setVal] = useState(options[0].values[0].value);
  const t = useTranslations("GPX");

  return (
    <Accordion type="single" collapsible className="rounded-t-xl bg-white">
      <AccordionItem
        key={t("DownloadGPX")}
        value={t("DownloadGPX")}
        className="border-b-stroke-secondary-dark w-full border-b border-t-0 "
      >
        <AccordionTrigger className="w-[300px] px-6 py-4 text-base font-bold md:px-6 md:text-xl [&[data-state=closed]>svg]:-rotate-90">
          {t("DownloadGPX")}
        </AccordionTrigger>
        <AccordionContent className="max-w-full px-6 md:max-w-[300px]">
          <p>{t("DownloadFullDescription")}</p>
          <form className="mt-4 md:mt-8">
            <RadioGroup
              defaultValue={val}
              onValueChange={(e: DownloadType) => setVal(e)}
            >
              {options.map(option => (
                <Fragment key={option.label}>
                  <span className="mb-2 block text-base font-bold">
                    {t(option.label)}
                  </span>
                  {option.values.map(value => (
                    <div className="flex items-center space-x-2" key={value.id}>
                      <RadioGroupItem value={value.value} id={value.id} />
                      <Label htmlFor={value.id}>{t(value.label)}</Label>
                    </div>
                  ))}
                </Fragment>
              ))}
            </RadioGroup>
            <div className="mt-8 flex w-full">
              <a
                download={
                  options
                    .flatMap(option => option.values)
                    .find(i => i.value == val)?.fileName || "HaervejenGPX"
                }
                href={`${baseURL()}/api/gpx/${
                  options
                    .flatMap(option => option.values)
                    .find(i => i.value == val)?.url || "base?type=hiking"
                }`}
                className="inline-flex h-10 w-full items-center justify-center gap-4 whitespace-nowrap rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:bg-inactive disabled:text-inactive-foreground"
              >
                {t("Download")}
                <Download strokeWidth="1" className="h-[18px] w-[18px]" />
              </a>
            </div>
          </form>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
